import { useEffect, useRef, useState } from "react";
import {
  AiReportStatus,
  getAiReportErrorMessage,
  getAiReportStatus,
  getLatestAiReport,
  isAiReportsDisabledError,
  startAiReport,
} from "../api/aiReports";
import { getAiReportGenerationReason } from "../utils/aiReportSchedule";

const POLL_INTERVAL_MS = 15_000;
const MAX_WAIT_MS = 10 * 60_000;
const NO_RESULT_MESSAGE = "Генерация завершилась без нового отчёта. Можно запустить её повторно вручную.";

type ReportState = {
  busy: boolean;
  running: boolean;
  html: string;
  updatedAt: string | null;
  message: string;
  error: string;
  timedOut: boolean;
  canGenerate: boolean;
};

const initialState: ReportState = {
  busy: true, running: false, html: "", updatedAt: null,
  message: "Загружаем ИИ-отчёт…", error: "", timedOut: false, canGenerate: false,
};

export function useAiReport(groupId: number, reportDays: number[]) {
  const scheduleKey = JSON.stringify(Array.from(new Set(reportDays)).sort((a, b) => a - b));
  const [state, setState] = useState(initialState);
  const actions = useRef({ refresh: () => {} });

  useEffect(() => {
    const scheduleDays = JSON.parse(scheduleKey) as number[];
    const controller = new AbortController();
    const { signal } = controller;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let busy = false;
    let current = { ...initialState };
    let lastStatus: AiReportStatus | null = null;
    let watching = false;
    let baseline: string | null = null;
    let waitingSince = 0;

    function publish(patch: Partial<ReportState>) {
      if (disposed) return;
      current = { ...current, ...patch };
      setState(current);
    }

    function clearTimer() {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    }

    function fail(error: unknown) {
      clearTimer();
      if (isAiReportsDisabledError(error)) {
        watching = false;
        publish({ running: false, canGenerate: false, error: "ИИ-отчёт недоступен. Обратитесь к администратору.", message: "" });
      } else {
        publish({ error: getAiReportErrorMessage(error), message: "" });
      }
    }

    // Кнопки и таймер используют одну очередь: запросы не пересекаются.
    async function perform(operation: () => Promise<void>) {
      if (disposed || busy) return;
      busy = true;
      clearTimer();
      publish({ busy: true, error: "" });
      try {
        await operation();
      } catch (error) {
        if (!signal.aborted) fail(error);
      } finally {
        busy = false;
        publish({ busy: false });
      }
    }

    function beginWatching(previousUpdatedAt: string | null) {
      if (watching) return;
      watching = true;
      baseline = previousUpdatedAt;
      waitingSince = Date.now();
    }

    function schedulePoll() {
      if (disposed) return;
      clearTimer();
      const remaining = MAX_WAIT_MS - (Date.now() - waitingSince);
      if (remaining <= 0) {
        publish({ timedOut: true, message: "Генерация занимает больше времени" });
        return;
      }
      timer = setTimeout(() => {
        timer = undefined;
        if (Date.now() - waitingSince >= MAX_WAIT_MS) {
          publish({ timedOut: true, message: "Генерация занимает больше времени" });
        } else {
          void perform(() => checkStatus());
        }
      }, Math.min(POLL_INTERVAL_MS, remaining));
    }

    async function applyStatus(status: AiReportStatus, latestMissing = false) {
      if (disposed) return;
      lastStatus = status;
      const reason = getAiReportGenerationReason(status, scheduleDays);
      publish({ running: status.is_running, canGenerate: reason === "missing" || reason === "outdated", timedOut: false, message: "" });
      if (status.is_running) beginWatching(status.updated_at);

      const completed = watching && !status.is_running;
      const newer = status.updated_at !== null && (baseline === null || Date.parse(status.updated_at) > Date.parse(baseline));
      const newResult = status.has_latest && (!completed || newer);
      // После ошибки загрузки можно показать предыдущий HTML, даже если обновление не удалось.
      const needsHtml = status.has_latest && (!current.html || (newResult && current.updatedAt !== status.updated_at));

      if (needsHtml && !latestMissing) {
        try {
          const html = await getLatestAiReport(groupId, signal);
          if (disposed) return;
          publish({ html, updatedAt: status.updated_at });
        } catch (error: any) {
          if (disposed) return;
          if (error?.response?.status !== 404) throw error;
          // HTML мог исчезнуть между /status и /latest. Не повторяем /latest в цикле.
          const refreshed = await getAiReportStatus(groupId, signal);
          await applyStatus(refreshed, true);
          return;
        }
      }

      if (status.is_running) {
        publish({ message: current.html ? "Отчёт обновляется. Предыдущий отчёт остаётся доступен." : "Отчёт формируется. Обычно это занимает 4–7 минут." });
        if (latestMissing && status.has_latest) publish({ error: "Готовый отчёт пока недоступен. Попробуйте открыть его позже." });
        schedulePoll();
      } else if (latestMissing && status.has_latest) {
        watching = false;
        publish({ error: "Готовый отчёт не найден. Попробуйте открыть его позже." });
      } else if (completed) {
        watching = false;
        if (!newResult) publish({ message: current.canGenerate ? NO_RESULT_MESSAGE : "Генерация завершилась без нового отчёта." });
      } else if (!status.has_latest) {
        publish({ message: "Готового отчёта пока нет. Можно запустить генерацию вручную." });
      } else if (reason === "outdated") {
        publish({ message: "Отчёт за последний день по расписанию не появился. Можно запустить генерацию вручную." });
      } else if (reason === "unknown") {
        publish({ error: "Не удалось определить актуальность отчёта. Попробуйте открыть его позже." });
      }
    }

    async function checkStatus() {
      if (disposed) return;
      const status = await getAiReportStatus(groupId, signal);
      await applyStatus(status);
    }

    async function launch() {
      if (disposed || current.running || !current.canGenerate) return;
      beginWatching(lastStatus?.updated_at ?? null);
      publish({ running: true, timedOut: false, message: "Отчёт формируется. Обычно это занимает 4–7 минут." });
      try {
        await startAiReport(groupId, signal);
        if (!disposed) schedulePoll();
      } catch (error: any) {
        if (disposed) return;
        if (!error?.response || error.response.status === 409) {
          // При потере ответа POST мог выполниться. Сначала выясняем состояние сервера.
          await checkStatus();
        } else {
          // Состояние после ошибки неизвестно до следующего /status.
          throw error;
        }
      }
    }

    actions.current = {
      refresh: () => {
        if (current.running) return;
        void perform(async () => {
          // За время просмотра отчёт мог запуститься с другого клиента.
          await checkStatus();
          if (!disposed && !current.running && current.canGenerate && !current.error) await launch();
        });
      },
    };

    publish(initialState);
    void perform(() => checkStatus());

    return () => {
      disposed = true;
      clearTimer();
      controller.abort();
    };
  }, [groupId, scheduleKey]);

  return { ...state, refresh: () => actions.current.refresh() };
}
